<script lang="ts" setup>
	import { ref, watch, onMounted, onUnmounted, nextTick } from "vue";
	import SeanIconFlat from "./SeanIconFlat.vue";

	const props = withDefaults(
		defineProps<{
			height?: number;
			width?: number;
			isRunning?: boolean;
			enableTooltip?: boolean;
		}>(),
		{
			enableTooltip: true,
			height: 24,
			width: 24
		}
	);

	const showTooltip = ref(false);
	const seanVideoReady = ref<HTMLVideoElement | null>(null);
	const seanVideoRunning = ref<HTMLVideoElement | null>(null);
	const seanVideoTooltip = ref<HTMLVideoElement | null>(null);

	const toggleTooltip = (b: boolean) => {
		if (!props.enableTooltip) {
			return;
		}
		showTooltip.value = b;
	};

	const safePlay = (video: HTMLVideoElement) => {
		video.play().catch(() => {});
	};

	const resumeActiveVideos = () => {
		const activeVideo = props.isRunning ? seanVideoRunning.value : seanVideoReady.value;
		if (activeVideo) safePlay(activeVideo);
		if (seanVideoTooltip.value && showTooltip.value) safePlay(seanVideoTooltip.value);
	};

	watch(
		() => props.isRunning,
		(running) => {
			const video = running ? seanVideoRunning.value : seanVideoReady.value;
			if (video) safePlay(video);
		}
	);

	const handleVisibilityChange = () => {
		if (document.visibilityState === "visible") {
			[seanVideoReady.value, seanVideoRunning.value].forEach((video) => {
				if (video) {
					video.addEventListener("canplay", () => video.play().catch(() => {}), { once: true });
					video.load();
				}
			});
		}
	};

	const handlePageShow = (event: PageTransitionEvent) => {
		if (event.persisted) resumeActiveVideos();
	};

	onMounted(() => {
		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("pageshow", handlePageShow);
		nextTick(resumeActiveVideos);
	});

	onUnmounted(() => {
		document.removeEventListener("visibilitychange", handleVisibilityChange);
		window.removeEventListener("pageshow", handlePageShow);
		// LSP-16687: an autoplay+loop <video> removed from the DOM but left playing is kept alive by the
		// browser as a media "pending activity" (a C++ persistent root). Via el.__vueParentComponent that
		// pins this whole component instance + its reactive scope, leaking on every unmount. Pause and
		// detach the source so the browser can release the elements.
		[seanVideoReady, seanVideoRunning, seanVideoTooltip].forEach((videoRef) => {
			const video = videoRef.value;
			if (!video) return;
			video.pause();
			video.removeAttribute("src");
			video.load();
		});
	});
</script>
<template>
	<div
		:class="{
			'sean-icon': true,
			'sean-icon-running': isRunning,
			'sean-icon-ready': !isRunning
		}"
		:style="{
			width: `${width}px`,
			height: `${height}px`
		}"
		@mouseenter="toggleTooltip(true)"
		@mouseleave="toggleTooltip(false)">
		<div
			class="sean-icon-inner"
			:style="{
				width: `${width}px`,
				height: `${height}px`
			}">
			<!-- Render both videos and toggle visibility to avoid loading delay -->
			<video
				v-show="!isRunning"
				ref="seanVideoReady"
				src="/sean-ready.mp4"
				poster="/sean.png"
				playsinline
				preload="auto"
				autoplay
				loop
				muted
				class="video-ready"
				:style="`width: ${width}px; height: ${height}px; object-fit: cover;`"></video>
			<video
				v-show="isRunning"
				ref="seanVideoRunning"
				src="/sean-running.mp4"
				poster="/sean.png"
				playsinline
				autoplay
				preload="auto"
				loop
				muted
				class="video-running"
				:style="`width: ${width}px; height: ${height}px; object-fit: cover;`"></video>
		</div>
		<div v-if="showTooltip && enableTooltip" class="tooltip">
			<div class="tooltip-inner">
				<video
					ref="seanVideoTooltip"
					src="/sean-ready.mp4"
					poster="/sean.png"
					playsinline
					autoplay
					loop
					muted
					:style="`width: ${64}px; height: ${64}px; object-fit: cover;`"></video>
			</div>
		</div>
	</div>
</template>
<style scoped>
	.sean-icon {
		position: relative;
	}
	.sean-icon-inner {
		border-radius: 50%;
		overflow: hidden;
		border: 1px solid #ccc;
		/* The poster fallback (/sean.png) is a cut-out with a transparent background, so when the
		   mp4 has not loaded the UI behind the icon showed through. The mp4 itself is opaque, so this
		   white backing is only ever visible in the fallback state (LSP-21250). */
		background: #fff;
		/* Ensure circular clipping in Safari/iOS */
		-webkit-clip-path: circle(50% at 50% 50%);
		clip-path: circle(50% at 50% 50%);
		/* Fallback mask for older iOS Safari */
		-webkit-mask-image: radial-gradient(circle at 50% 50%, #000 99%, transparent 100%);
		mask-image: radial-gradient(circle at 50% 50%, #000 99%, transparent 100%);
		-webkit-mask-size: 100% 100%;
		mask-size: 100% 100%;
		-webkit-mask-repeat: no-repeat;
		mask-repeat: no-repeat;
	}
	.sean-icon-inner video {
		/* Fallback rounding for Safari versions that don't clip descendants */
		border-radius: 50%;
		display: block;
	}
	/* Target specific videos regardless of parent state to ensure correct positioning */
	.video-ready {
		object-position: 40% 20%;
	}
	.video-running {
		object-position: 60% 20%;
	}

	.tooltip {
		position: absolute;
		left: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-left: 10px;
		z-index: 1000;
		background: white;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		padding: 8px;
		border: 1px solid #e0e0e0;
	}
	.tooltip-inner {
		border-radius: 50%;
		width: 64px;
		height: 64px;
		overflow: hidden;
		background: #fff;
		/* Mirror mask fallback for tooltip preview */
		-webkit-mask-image: radial-gradient(circle at 50% 50%, #000 99%, transparent 100%);
		mask-image: radial-gradient(circle at 50% 50%, #000 99%, transparent 100%);
		-webkit-mask-size: 100% 100%;
		mask-size: 100% 100%;
		-webkit-mask-repeat: no-repeat;
		mask-repeat: no-repeat;
	}
	.tooltip-inner video {
		border-radius: 50%;
		display: block;
	}
	.mobile .tooltip {
		display: none;
	}
</style>
